import styles from './LocationPicker.module.scss';

import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { Loader } from 'semantic-ui-react';
import PlacesAutocomplete, {
  getLatLng,
  geocodeByAddress
} from 'react-places-autocomplete';

import { fetchCurrentLocation } from '../../../util/location';
import { useFormikContext } from 'formik';

type LocationPickerProps = {
  name: string;
  className: string;
  placeholder: string;
  initialValue: any;
  disabled?: boolean;
  searchOptions?: {
    componentRestrictions: {
      country: string;
    };
  };
  locationFieldName: string;
  coordinatesFieldName?: string;
  showCurrentLocationButton?: boolean;
};

const LocationPicker: React.FC<LocationPickerProps> = ({
  initialValue,
  className,
  placeholder,
  searchOptions,
  locationFieldName,
  coordinatesFieldName,
  showCurrentLocationButton,
  disabled
}) => {
  const [location, setLocation] = useState('');
  const [geocode, setGeocode] = useState<number[]>([]);
  const [locating, setLocating] = useState(false);

  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    setLocation(initialValue[locationFieldName]);
  }, [initialValue[locationFieldName]]);

  const handleChange = (location) => {
    if (!disabled) {
      setLocation(location);
      setFieldValue(locationFieldName, location);
    }
  };

  const handleSelect = (address) => {
    setFieldValue(locationFieldName, address);

    geocodeByAddress(address)
      .then((results) => getLatLng(results[0]))
      .then((latLng) => {
        setGeocode([latLng.lng, latLng.lat]);
        if (coordinatesFieldName) {
          setFieldValue(coordinatesFieldName, [latLng.lng, latLng.lat]);
        }
      });
  };

  const handleClick = (address) => {
    handleChange(address);
    handleSelect(address);
  };

  const setCurrentLocation = async () => {
    try {
      setLocating(true);
      const coords = await fetchCurrentLocation();

      setLocation(coords.location);
      setGeocode([coords.longitude, coords.latitude]);
      setFieldValue(locationFieldName, coords.location);
      if (coordinatesFieldName) {
        setFieldValue(coordinatesFieldName, [
          coords.longitude,
          coords.latitude
        ]);
      }

      setLocating(false);
    } catch (e) {
      setLocating(false);
    }
  };

  return (
    <>
      <PlacesAutocomplete
        value={location}
        onChange={handleChange}
        onSelect={handleSelect}
        searchOptions={searchOptions}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div className={styles.AutoCompleteWrapper}>
            <input
              data-cy="location-input"
              value={initialValue[locationFieldName]}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...getInputProps({
                placeholder: placeholder || 'Enter your Location',
                className
              })}
            />
            {showCurrentLocationButton && (
              <>
                {loading || locating ? (
                  <Loader size="mini" active className={styles.InputIcon} />
                ) : (
                  <img
                    onClick={setCurrentLocation}
                    src="/img/icons/marker.svg"
                    className={styles.InputIcon}
                  />
                )}
              </>
            )}
            <div
              className={cn(styles.Suggestions, {
                [styles.Active]: suggestions && !!suggestions.length
              })}
            >
              {suggestions.map((suggestion) => {
                const className = cn(styles.SuggestionItem, {
                  [styles.Active]: suggestion.active
                });

                return (
                  <div
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...getSuggestionItemProps(suggestion, {
                      className,
                      onClick: () => {
                        handleClick(suggestion.description);
                      }
                    })}
                    key={suggestion.placeId}
                  >
                    <span>{suggestion.description}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PlacesAutocomplete>

      <input type="hidden" value={location} name={locationFieldName} />
      <input
        type="hidden"
        value={geocode.join(',')}
        name={coordinatesFieldName}
      />
    </>
  );
};

export default LocationPicker;
